import React from 'react';
import {
  Card,
  Typography,
  Box,
  Avatar,
  Button,
  SxProps,
  Chip,
  Stack,
  ButtonProps,
  ChipProps,
  Fade,
} from '@mui/material';

import StarIcon from '@mui/icons-material/Star';

import { TP_COLORS } from '../../../../constants';
import { getShortName } from '../../../../helper';
import { getRandomTutorBio, getTotalExperienceYears } from '../../Tutor';
import { ITutor } from '../../../../store/V2/TutorSlice';

const cardStyles: SxProps = {
  borderRadius: '20px',
  padding: '10px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
};

const boxStyles: SxProps = {
  display: 'flex',
  alignItems: 'center',
  background: `url('${process.env.PUBLIC_URL}/images/tutorProfile/bg-tutor-card.png') 0px -15px`,
  color: '#fff',
  padding: '20px',
  borderRadius: '10px',
};

const avatarStyles: SxProps = {
  marginRight: '20px',
  width: '68px',
  height: '68px',
  fontSize: '36px',
  border: '2px solid #F8FAFC',
};

const chipProps: ChipProps = {
  size: 'small',
  sx: {
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '4px',
    backgroundColor: TP_COLORS.yellow.light,
    marginRight: '5px',
  },
};

const buttonProps: ButtonProps = {
  disableElevation: true,
  variant: 'contained',
  color: 'secondary',
  sx: {
    borderRadius: '25px',
    padding: '4px 20px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: TP_COLORS.gray.darken,
  },
};

interface TutorCardProps {
  tutorData: ITutor;
  handleSelect: (tutorId: string) => void;
}

const TutorCard: React.FC<TutorCardProps> = ({ tutorData, handleSelect }) => {
  const experienceYears = getTotalExperienceYears(
    tutorData.profile?.experiences || []
  );
  const lessonDelivered =
    (tutorData?.tutorDetails?.lessonDelivered || 0) > 250
      ? tutorData?.tutorDetails?.lessonDelivered
      : 250;

  return (
    <Card sx={cardStyles}>
      <Box sx={boxStyles}>
        <Avatar
          src={tutorData.avatar}
          alt={tutorData.firstname}
          sx={avatarStyles}
        />

        <Typography fontSize="18px" fontWeight="600">
          {getShortName(tutorData.firstname, tutorData.lastname)}
        </Typography>
      </Box>

      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px',
        }}
      >
        <Box display="flex" alignItems="center">
          <Chip {...chipProps} label={experienceYears} />
          <Typography fontSize="12px" color="#647488">
            of experience
          </Typography>
        </Box>
        <Fade in={Boolean(tutorData.rate)}>
          <Typography fontSize="16px" fontWeight="600">
            {tutorData.rate?.amount}
            <span style={{ fontSize: '14px', marginLeft: '2px' }}>
              {tutorData.rate?.currency}/hr
            </span>
          </Typography>
        </Fade>
      </Stack>

      <Typography
        fontSize="12px"
        color="#94A3B8"
        margin="10px"
        className="ellipsis-text two-lines"
      >
        {getRandomTutorBio(
          tutorData._id,
          `${tutorData.firstname} ${tutorData.lastname || ''}`,
          experienceYears,
          lessonDelivered
        )}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px',
        }}
      >
        <Stack>
          <Typography fontSize="12px">
            <span style={{ fontWeight: '600', color: TP_COLORS.green.light }}>
              {lessonDelivered}+{' '}
            </span>
            lessons delivered
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              marginTop: '8px',
            }}
          >
            <StarIcon
              fontSize="small"
              sx={{
                color: TP_COLORS.yellow.light,
              }}
            />
            <Typography fontSize="12px" fontWeight="600">
              {(Number(tutorData.tutorDetails?.adminRating) || 7).toFixed(1)}
            </Typography>
            <Typography fontSize="12px">ratings</Typography>
          </Box>
        </Stack>
        <Button {...buttonProps} onClick={() => handleSelect(tutorData._id)}>
          Select
        </Button>
      </Box>
    </Card>
  );
};

export default TutorCard;
